import {
  CloseButton,
  Combobox,
  InputBase,
  ScrollArea,
  useCombobox,
} from '@mantine/core'
import React, { useMemo, useState } from 'react'

import { Icon } from '@components/icon.component'
import { TextInput } from '@components/mantine/text-input.component'

type GroupedSelectItem = {
  value: string
  label: string
  disabled?: boolean
}

type GroupedSelectGroup = {
  group: string
  icon?: string
  items: GroupedSelectItem[]
}

type RenderTriggerProps = {
  value: string | null | undefined
  selectedLabel: string | null
  toggle: () => void
  isOpen: boolean
  disabled: boolean
}

type GroupedSelectProps = {
  data: GroupedSelectGroup[]
  value?: string | null
  onChange?: (value: string | null) => void
  placeholder?: string
  disabled?: boolean
  searchable?: boolean
  clearable?: boolean
  fullWidth?: boolean
  className?: string
  nothingFoundMessage?: string
  renderTrigger?: (props: RenderTriggerProps) => React.ReactNode
}

export function GroupedSelect({
  data,
  value,
  onChange,
  placeholder = 'Select...',
  disabled = false,
  searchable = false,
  clearable = false,
  fullWidth = false,
  className = '',
  nothingFoundMessage = 'No options found',
  renderTrigger,
}: GroupedSelectProps) {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      setSearch('')
    },
    onDropdownOpen: () => {
      combobox.focusSearchInput()
    },
  })

  const [search, setSearch] = useState('')

  // Find the selected item's label
  const selectedLabel = useMemo(() => {
    for (const group of data) {
      const item = group.items.find(item => item.value === value)
      if (item) return item.label
    }
    return null
  }, [data, value])

  // Filter items based on search
  const filteredData = useMemo(() => {
    if (!search) return data

    const searchLower = search.toLowerCase().trim()
    return data
      .map(group => ({
        ...group,
        items: group.items.filter(item =>
          item.label.toLowerCase().includes(searchLower),
        ),
      }))
      .filter(group => group.items.length > 0)
  }, [data, search])

  const hasOptions = filteredData.some(group => group.items.length > 0)

  const options = filteredData.map(group => (
    <Combobox.Group key={group.group}>
      <div
        className="relative overflow-hidden whitespace-nowrap text-ellipsis cursor-default select-none"
        style={{ padding: '0.8rem 1.6rem 0.8rem 0', fontSize: '1.3rem' }}
      >
        {/* Horizontal line behind the text */}
        <div
          className="absolute bg-grey-100"
          style={{
            left: 0,
            right: '1.6rem',
            top: '1.7rem',
            height: '0.1rem',
            zIndex: 0,
          }}
        />
        {/* Group label with white background to "cut through" line */}
        <div
          className="inline-flex items-center bg-white text-grey-500"
          style={{ padding: '0 1.2rem', position: 'relative', zIndex: 1 }}
        >
          {group.icon && (
            <Icon
              name={group.icon}
              size={14}
              className="mr-2"
              style={{ marginTop: '-0.2rem' }}
            />
          )}
          {group.group}
        </div>
      </div>
      {group.items.map(item => (
        <Combobox.Option
          key={item.value}
          value={item.value}
          disabled={item.disabled}
          className="px-4 py-3 rounded-md cursor-pointer text-xl! data-[combobox-selected=true]:bg-grey-50 data-[combobox-active=true]:bg-grey-100"
        >
          {item.label}
        </Combobox.Option>
      ))}
    </Combobox.Group>
  ))

  return (
    <Combobox
      store={combobox}
      withinPortal
      portalProps={{ target: '#select-portal' }}
      position="bottom-start"
      offset={4}
      middlewares={{ flip: true, shift: true }}
      onOptionSubmit={val => {
        onChange?.(val)
        combobox.closeDropdown()
      }}
    >
      <Combobox.Target>
        {renderTrigger ? (
          renderTrigger({
            value,
            selectedLabel,
            toggle: () => combobox.toggleDropdown(),
            isOpen: combobox.dropdownOpened,
            disabled,
          })
        ) : (
          <InputBase
            component="button"
            type="button"
            pointer
            rightSection={
              clearable && value ? (
                <CloseButton
                  size="sm"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => onChange?.(null)}
                  aria-label="Clear selection"
                />
              ) : (
                <Combobox.Chevron />
              )
            }
            rightSectionPointerEvents={clearable && value ? 'all' : 'none'}
            onClick={() => combobox.toggleDropdown()}
            disabled={disabled}
            size="lg"
            classNames={{
              input: `form-input text-xl ${fullWidth ? 'w-full' : ''} ${className}`,
              wrapper: 'bg-transparent',
            }}
          >
            {selectedLabel || (
              <span className="text-grey-400 text-xl">{placeholder}</span>
            )}
          </InputBase>
        )}
      </Combobox.Target>

      <Combobox.Dropdown className="bg-white shadow-lg rounded-lg border border-grey-100 p-2">
        {searchable && (
          <div className="p-2">
            <TextInput
              value={search}
              onChange={setSearch}
              placeholder="Search..."
              autoFocus
            />
          </div>
        )}
        <ScrollArea.Autosize mah={300}>
          <Combobox.Options>
            {hasOptions ? (
              options
            ) : (
              <Combobox.Empty className="text-center py-4 text-grey-400">
                {nothingFoundMessage}
              </Combobox.Empty>
            )}
          </Combobox.Options>
        </ScrollArea.Autosize>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export type { GroupedSelectItem, GroupedSelectGroup, RenderTriggerProps }
